import { Link } from "react-router-dom";
import { Home, Users, ListChecks, Activity, BellRing, Zap, Landmark, CalendarDays, FileSignature, ScanLine, Map as MapIcon, Camera, Wallet, BarChart3, ListTodo } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatTL, formatDate } from "@/lib/api";
import { LISTING_TYPES } from "@/lib/portfolioOptions";
import { CUSTOMER_TYPES } from "@/lib/crmOptions";
import { Panel, Empty, Row } from "@/components/dashboard/Panel";

const fmtTime = (s) => new Date(s).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const STATUS = { ACTIVE: ["Yayında", "bg-emerald-50 text-emerald-700 border-emerald-200"], SOLD: ["Satıldı", "bg-blue-50 text-blue-700 border-blue-200"], RENTED: ["Kiralandı", "bg-blue-50 text-blue-700 border-blue-200"] };

export const RecentPortfolios = ({ items, className }) => (
  <Panel title="Son eklenen portföyler" icon={Home} link="/portfolios" testId="panel-recent-portfolios" className={className}>
    {items.length === 0 ? <Empty>Henüz portföy eklenmedi</Empty> : items.map((p) => (
      <Row key={p.id} testId={`recent-portfolio-${p.id}`} title={p.title} sub={`${LISTING_TYPES[p.listing_type] || ""} · ${[p.district, p.city].filter(Boolean).join(", ")}`}
        right={<><div className="text-sm font-semibold text-slate-900">{formatTL(p.price)}</div>
          <Badge variant="outline" className={`mt-0.5 text-[10px] ${STATUS[p.status]?.[1]}`}>{STATUS[p.status]?.[0]}</Badge></>} />
    ))}
  </Panel>
);

export const RecentCustomers = ({ items }) => (
  <Panel title="Son kaydedilen müşteriler" icon={Users} link="/customers" testId="panel-recent-customers">
    {items.length === 0 ? <Empty>Henüz müşteri kaydı yok</Empty> : items.map((c) => (
      <Row key={c.id} testId={`recent-customer-${c.id}`} title={c.full_name} sub={CUSTOMER_TYPES[c.customer_type] || c.customer_type}
        right={<span className="text-xs text-slate-500">{formatDate(c.created_at)}</span>} />
    ))}
  </Panel>
);

export const PendingWork = ({ todos, deeds, showDeeds }) => (
  <Panel title="Bekleyen işlemler" icon={ListChecks} link="/todos" testId="panel-pending">
    {todos.length === 0 && (!showDeeds || deeds.length === 0) ? <Empty>Bekleyen iş yok</Empty> : (
      <>
        {todos.map((t) => (
          <Row key={t.id} testId={`pending-todo-${t.id}`} title={t.title} sub="Görev"
            right={<span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-50 text-amber-700">{t.due_date ? formatDate(t.due_date) : "Tarihsiz"}</span>} />
        ))}
        {showDeeds && deeds.map((d) => (
          <Row key={d.id} testId={`pending-deed-${d.id}`} title={d.title} sub="Tapu işlemi"
            right={<span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-blue-50 text-blue-700">{d.appointment_at ? formatDate(d.appointment_at) : "Devam ediyor"}</span>} />
        ))}
      </>
    )}
  </Panel>
);

export const RecentActivity = ({ items }) => (
  <Panel title="Son aktiviteler" icon={Activity} testId="panel-activity">
    {items.length === 0 ? <Empty>Kayıt yok</Empty> : (
      <ol className="relative border-l border-slate-200 ml-1.5 space-y-3">
        {items.map((a) => (
          <li key={a.id} data-testid={`activity-${a.id}`} className="pl-4 relative">
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-50" />
            <div className="text-sm text-slate-800">{a.action}</div>
            <div className="text-[11px] text-slate-500">{a.username} · {fmtTime(a.created_at)}</div>
          </li>
        ))}
      </ol>
    )}
  </Panel>
);

const today = () => new Date().toISOString().slice(0, 10);

export function Reminders({ todos, deeds, subscription, isDemo }) {
  const items = [];
  if (subscription && subscription.status !== "ACTIVE" && subscription.status !== "FROZEN")
    items.push({ id: "sub", tone: "amber", title: `${isDemo ? "Demo" : "Abonelik"} süresi: ${subscription.days_left} gün kaldı`, sub: `Bitiş ${formatDate(subscription.period_end)}` });
  todos.filter((t) => t.due_date && t.due_date.slice(0, 10) <= today()).forEach((t) =>
    items.push({ id: `t-${t.id}`, tone: t.due_date.slice(0, 10) < today() ? "red" : "amber", title: t.title, sub: t.due_date.slice(0, 10) < today() ? "Süresi geçmiş görev" : "Bugün bitmesi gereken görev" }));
  const week = Date.now() + 7 * 864e5;
  deeds.filter((d) => d.appointment_at && new Date(d.appointment_at) >= new Date() && new Date(d.appointment_at) <= week).forEach((d) =>
    items.push({ id: `d-${d.id}`, tone: "blue", title: d.title, sub: `Tapu randevusu · ${fmtTime(d.appointment_at)}` }));
  const dot = { amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500" };
  return (
    <Panel title="Hatırlatmalar" icon={BellRing} testId="panel-reminders">
      {items.length === 0 ? <Empty>Şu an hatırlatma yok</Empty> : items.map((i) => (
        <div key={i.id} data-testid={`reminder-${i.id}`} className="flex gap-3 py-2.5 border-b border-slate-100 last:border-0">
          <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot[i.tone]}`} />
          <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{i.title}</div><div className="text-xs text-slate-500">{i.sub}</div></div>
        </div>
      ))}
    </Panel>
  );
}

const QUICK = [
  { to: "/portfolios", icon: Home, label: "Portföy", feature: "PORTFOLIO" },
  { to: "/customers", icon: Users, label: "Müşteriler", feature: "CRM" },
  { to: "/calendar", icon: CalendarDays, label: "Takvim" },
  { to: "/todos", icon: ListTodo, label: "Yapılacaklar" },
  { to: "/map", icon: MapIcon, label: "Harita", feature: "MAP" },
  { to: "/shoots", icon: Camera, label: "Çekim Günü", feature: "SHOOTS" },
  { to: "/scans", icon: ScanLine, label: "Belge Tarama", feature: "SCAN" },
  { to: "/contracts", icon: FileSignature, label: "Sözleşmeler", feature: "CONTRACT" },
  { to: "/deeds", icon: Landmark, label: "Tapu Takip", feature: "DEED" },
  { to: "/finance", icon: Wallet, label: "Gelir / Gider", feature: "FINANCE", roles: ["BROKER", "ACCOUNTANT"] },
  { to: "/reports", icon: BarChart3, label: "Raporlar", feature: "PERFORMANCE" },
];

export const QuickAccess = ({ role, hasFeature, className }) => (
  <Panel title="Hızlı erişim" icon={Zap} testId="panel-quick-access" className={className}>
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
      {QUICK.filter((q) => (!q.feature || hasFeature(q.feature)) && (!q.roles || q.roles.includes(role))).map((q) => (
        <Link key={q.to} to={q.to} data-testid={`quick-${q.to.slice(1)}`}
          className="group flex items-center gap-2.5 rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-3 text-sm font-medium text-slate-700 hover:border-emerald-200 hover:bg-emerald-50/70 hover:text-emerald-800 transition-colors">
          <q.icon className="w-4 h-4 text-emerald-700 shrink-0" /><span className="truncate">{q.label}</span>
        </Link>
      ))}
    </div>
  </Panel>
);
